/**
 * REVELA Internal — esquema único de datos.
 *
 * Este archivo es la fuente de verdad para:
 *  - el script que crea las bases en Notion (scripts/setup-notion.ts)
 *  - las funciones de Cloudflare que leen y escriben en Notion
 *  - los formularios de la app
 *
 * Si agregan un campo acá, aparece en el formulario y se guarda en Notion.
 * (Para que exista en Notion, vuelvan a correr `npm run setup:notion`,
 *  que agrega las columnas que falten sin borrar nada.)
 */

export type FieldType =
  | 'title'     // título de la página en Notion
  | 'text'      // texto corto
  | 'longtext'  // texto largo (guiones, notas, contratos)
  | 'number'
  | 'money'     // número en USD
  | 'select'
  | 'multi'     // selección múltiple / etiquetas
  | 'date'
  | 'datetime'
  | 'url'
  | 'files'     // lista de archivos (enlaces de la app o URLs externas), uno por línea
  | 'image'     // un archivo de imagen (ruta o URL)
  | 'phone'
  | 'email'
  | 'relation'
  | 'checkbox'
  | 'json'      // datos estructurados (entradas de calculadoras)

export type CollectionKey =
  | 'propiedades'
  | 'clientes'
  | 'visitas'
  | 'marketing'
  | 'academia'
  | 'documentos'
  | 'tareas'
  | 'evaluaciones'

export interface Field {
  key: string
  label: string
  type: FieldType
  /** Nombre de la columna en Notion */
  notion: string
  options?: string[]
  relation?: CollectionKey
  /** relación de un solo registro (en la app se muestra como selector simple) */
  single?: boolean
  /** Esta relación crea la columna espejo en la otra base (con este nombre de campo) */
  dual?: string
  /** Campo espejo, lo crea Notion automáticamente desde la otra base */
  mirror?: boolean
  section?: string
  showIf?: { key: string; in: string[] }
  hidden?: boolean
  help?: string
  placeholder?: string
  required?: boolean
  bucket?: 'public' | 'private'
}

export interface Collection {
  key: CollectionKey
  db: string
  env: string
  prefix: string
  label: string
  singular: string
  fields: Field[]
}

export const SOCIAS = ['Leticia', 'Yesica']

export const PILARES = [
  'Build in Public',
  'Aprendemos Juntas',
  'Hay más de lo que ves',
  'La casa de tus sueños',
  'Invertir desde afuera',
  'Leti & Yesi',
]

export const CATEGORIAS_ACADEMIA = [
  'Marca personal y posicionamiento',
  'Marketing inmobiliario · redes y ventas',
  'Captación y presentación de propiedades',
  'Home staging y diseño de interiores',
  'Inversión y análisis de rentabilidad',
  'Tecnología · IA y automatizaciones',
  'Organización y gestión de una inmobiliaria',
  'Finanzas y aspectos legales',
]

export const ESTADOS_PROPIEDAD = ['Captación', 'Disponible', 'Reservada', 'Vendida']
export const ESTADOS_COMERCIALES = ['Nuevo', 'Contactado', 'Calificado', 'Visitando', 'Negociando', 'Cerrado', 'Perdido']
export const ESTADOS_PUBLICACION = ['Idea', 'Grabado', 'Publicado']
export const ESTADOS_ACADEMIA = ['Idea', 'En preparación', 'Grabado', 'Editado', 'Listo para publicar']
export const ESTADOS_VISITA = ['Programada', 'Confirmada', 'Realizada', 'Cancelada']
export const ESTADOS_RECORDATORIO = ['Pendiente', 'Enviado', 'Error', 'Desactivado']
export const ESTADOS_TAREA = ['Por hacer', 'En curso', 'Hecha']
export const TIPOS_CONTENIDO = ['Publicación', 'Idea', 'Guion', 'Texto listo', 'Frase de marca', 'Foto', 'Referencia visual']
export const TIPOS_DOCUMENTO = [
  'Exclusividad y mediación',
  'Boleto de reserva',
  'Arrendamiento',
  'Legal de propiedad',
  'Debida diligencia',
  'Otro',
]

const socia = (key = 'socia', label = 'Socia responsable'): Field => ({
  key, label, type: 'select', notion: 'Socia', options: SOCIAS,
})

export const COLLECTIONS: Record<CollectionKey, Collection> = {
  propiedades: {
    key: 'propiedades', db: 'DB_Propiedades', env: 'NOTION_DB_PROPIEDADES', prefix: 'PROP',
    label: 'Propiedades', singular: 'propiedad',
    fields: [
      { key: 'nombre', label: 'Nombre de la propiedad', type: 'title', notion: 'Nombre', required: true, section: 'Ficha', placeholder: 'Casa del Bulevar' },
      { key: 'estado', label: 'Estado', type: 'select', notion: 'Estado', options: ESTADOS_PROPIEDAD, section: 'Ficha' },
      socia(),
      { key: 'precio', label: 'Precio (USD)', type: 'money', notion: 'Precio USD', section: 'Ficha' },
      { key: 'tipo', label: 'Tipo', type: 'select', notion: 'Tipo', options: ['Casa', 'Apartamento', 'Terreno', 'Chacra', 'Local', 'Oficina'], section: 'Ficha' },
      { key: 'direccion', label: 'Dirección', type: 'text', notion: 'Dirección', section: 'Ubicación', placeholder: 'Av. España 1234' },
      { key: 'ciudad', label: 'Barrio / ciudad', type: 'text', notion: 'Ciudad', section: 'Ubicación', placeholder: 'Paysandú' },
      { key: 'mapa', label: 'Enlace de Google Maps', type: 'url', notion: 'Google Maps', section: 'Ubicación', help: 'Si lo dejan vacío, se arma con la dirección.' },
      { key: 'dormitorios', label: 'Dormitorios', type: 'number', notion: 'Dormitorios', section: 'Características' },
      { key: 'banos', label: 'Baños', type: 'number', notion: 'Baños', section: 'Características' },
      { key: 'm2', label: 'Superficie (m²)', type: 'number', notion: 'm²', section: 'Características' },
      { key: 'terreno', label: 'Terreno (m²)', type: 'number', notion: 'Terreno m²', section: 'Características' },
      { key: 'descripcion', label: 'Descripción', type: 'longtext', notion: 'Descripción', section: 'Características' },
      { key: 'orientacion', label: 'Orientación solar', type: 'select', notion: 'Orientación', options: ['Norte', 'Noreste', 'Este', 'Sureste', 'Sur', 'Suroeste', 'Oeste', 'Noroeste'], section: 'Confort' },
      { key: 'iluminacion', label: 'Iluminación natural (1 a 5)', type: 'number', notion: 'Iluminación natural', section: 'Confort' },
      { key: 'ruido', label: 'Ruido medido (dB)', type: 'number', notion: 'Ruido dB', section: 'Confort', help: 'Solo si se midió. Referencia: 35 dB silencioso, 55 dB calle tranquila.' },
      { key: 'foto', label: 'Fotografía principal', type: 'image', notion: 'Foto principal', section: 'Fotos y documentos', bucket: 'public' },
      { key: 'galeria', label: 'Galería', type: 'files', notion: 'Galería', section: 'Fotos y documentos', bucket: 'public' },
      { key: 'legales', label: 'Documentación legal', type: 'files', notion: 'Documentación legal', section: 'Fotos y documentos', bucket: 'private' },
      { key: 'clientesInteresados', label: 'Clientes interesados', type: 'relation', relation: 'clientes', notion: 'Clientes interesados', mirror: true, hidden: true },
      { key: 'visitas', label: 'Visitas', type: 'relation', relation: 'visitas', notion: 'Visitas', mirror: true, hidden: true },
      { key: 'documentos', label: 'Documentos', type: 'relation', relation: 'documentos', notion: 'Documentos', mirror: true, hidden: true },
      { key: 'evaluaciones', label: 'Evaluaciones', type: 'relation', relation: 'evaluaciones', notion: 'Evaluaciones', mirror: true, hidden: true },
      { key: 'publicaciones', label: 'Publicaciones', type: 'relation', relation: 'marketing', notion: 'Publicaciones', mirror: true, hidden: true },
    ],
  },

  clientes: {
    key: 'clientes', db: 'DB_Clientes', env: 'NOTION_DB_CLIENTES', prefix: 'CLI',
    label: 'Clientes', singular: 'cliente',
    fields: [
      { key: 'nombre', label: 'Nombre', type: 'title', notion: 'Nombre', required: true, section: 'Contacto' },
      { key: 'segmento', label: 'Tipo de cliente', type: 'select', notion: 'Segmento', options: ['Comprador de vivienda', 'Inversor del exterior'], section: 'Contacto' },
      socia(),
      { key: 'estado', label: 'Estado comercial', type: 'select', notion: 'Estado comercial', options: ESTADOS_COMERCIALES, section: 'Contacto' },
      { key: 'telefono', label: 'Teléfono', type: 'phone', notion: 'Teléfono', section: 'Contacto' },
      { key: 'whatsapp', label: 'WhatsApp (con código de país)', type: 'phone', notion: 'WhatsApp', section: 'Contacto', placeholder: '+598 99 123 456' },
      { key: 'email', label: 'Email', type: 'email', notion: 'Email', section: 'Contacto' },
      { key: 'presupuesto', label: 'Presupuesto (USD)', type: 'money', notion: 'Presupuesto USD', section: 'Contacto' },
      // Compradores de vivienda
      { key: 'preferencias', label: 'Preferencias de vivienda', type: 'longtext', notion: 'Preferencias de vivienda', section: 'Perfil comprador', showIf: { key: 'segmento', in: ['Comprador de vivienda'] } },
      { key: 'familia', label: 'Necesidades familiares y estilo de vida', type: 'longtext', notion: 'Necesidades familiares', section: 'Perfil comprador', showIf: { key: 'segmento', in: ['Comprador de vivienda'] } },
      // Inversores del exterior
      { key: 'pais', label: 'País de residencia', type: 'text', notion: 'País de residencia', section: 'Perfil inversor', showIf: { key: 'segmento', in: ['Inversor del exterior'] } },
      { key: 'objetivos', label: 'Objetivos de inversión', type: 'multi', notion: 'Objetivos de inversión', options: ['Renta mensual', 'Revalorización', 'Retiro en Uruguay', 'Diversificar', 'Vivienda futura'], section: 'Perfil inversor', showIf: { key: 'segmento', in: ['Inversor del exterior'] } },
      { key: 'rentaEsperada', label: 'Rentabilidad esperada (% anual)', type: 'number', notion: 'Rentabilidad esperada %', section: 'Perfil inversor', showIf: { key: 'segmento', in: ['Inversor del exterior'] } },
      { key: 'prefInversion', label: 'Preferencias de inversión', type: 'longtext', notion: 'Preferencias de inversión', section: 'Perfil inversor', showIf: { key: 'segmento', in: ['Inversor del exterior'] } },
      { key: 'seguimiento', label: 'Seguimiento remoto (zona horaria, canal, frecuencia)', type: 'text', notion: 'Seguimiento remoto', section: 'Perfil inversor', showIf: { key: 'segmento', in: ['Inversor del exterior'] } },
      { key: 'propiedadesInteres', label: 'Propiedades de interés', type: 'relation', relation: 'propiedades', notion: 'Propiedades de interés', dual: 'clientesInteresados', section: 'Seguimiento' },
      { key: 'notas', label: 'Notas e historial de consultas', type: 'longtext', notion: 'Notas', section: 'Seguimiento' },
      { key: 'docsDD', label: 'Documentación de debida diligencia', type: 'files', notion: 'Debida diligencia', section: 'Cumplimiento', bucket: 'private' },
      { key: 'cumplimiento', label: 'Cumplimiento', type: 'select', notion: 'Cumplimiento', options: ['Pendiente', 'Aprobado'], section: 'Cumplimiento', hidden: true },
      { key: 'revisadoPor', label: 'Revisado por', type: 'select', notion: 'Revisado por', options: SOCIAS, section: 'Cumplimiento', hidden: true },
      { key: 'fechaAprobacion', label: 'Fecha de aprobación', type: 'date', notion: 'Fecha de aprobación', section: 'Cumplimiento', hidden: true },
      { key: 'visitas', label: 'Visitas', type: 'relation', relation: 'visitas', notion: 'Visitas', mirror: true, hidden: true },
      { key: 'documentos', label: 'Documentos', type: 'relation', relation: 'documentos', notion: 'Documentos', mirror: true, hidden: true },
    ],
  },

  visitas: {
    key: 'visitas', db: 'DB_Agenda_Visitas', env: 'NOTION_DB_AGENDA', prefix: 'VIS',
    label: 'Agenda', singular: 'cita',
    fields: [
      { key: 'titulo', label: 'Título', type: 'title', notion: 'Título', required: true, placeholder: 'Visita casa del Bulevar' },
      { key: 'tipo', label: 'Tipo', type: 'select', notion: 'Tipo', options: ['Visita', 'Reunión', 'Videollamada', 'Firma'] },
      { key: 'inicio', label: 'Fecha y hora', type: 'datetime', notion: 'Inicio', required: true },
      { key: 'duracion', label: 'Duración (minutos)', type: 'number', notion: 'Duración min' },
      socia('socia', 'Socia que acompaña'),
      { key: 'estado', label: 'Estado', type: 'select', notion: 'Estado', options: ESTADOS_VISITA },
      { key: 'cliente', label: 'Cliente', type: 'relation', relation: 'clientes', single: true, notion: 'Cliente', dual: 'visitas' },
      { key: 'propiedad', label: 'Propiedad', type: 'relation', relation: 'propiedades', single: true, notion: 'Propiedad', dual: 'visitas' },
      { key: 'lugar', label: 'Lugar (si no es una propiedad)', type: 'text', notion: 'Lugar' },
      { key: 'notas', label: 'Notas', type: 'longtext', notion: 'Notas' },
      { key: 'recordatorio', label: 'Recordatorio', type: 'select', notion: 'Recordatorio', options: ESTADOS_RECORDATORIO, hidden: true },
      { key: 'recordatorioError', label: 'Detalle del recordatorio', type: 'text', notion: 'Detalle recordatorio', hidden: true },
      { key: 'googleEventId', label: 'ID Google Calendar', type: 'text', notion: 'ID Google Calendar', hidden: true },
    ],
  },

  marketing: {
    key: 'marketing', db: 'DB_Marketing', env: 'NOTION_DB_MARKETING', prefix: 'MKT',
    label: 'Marketing', singular: 'contenido',
    fields: [
      { key: 'titulo', label: 'Título', type: 'title', notion: 'Título', required: true, section: 'Publicación' },
      { key: 'tipo', label: 'Tipo de contenido', type: 'select', notion: 'Tipo', options: TIPOS_CONTENIDO, section: 'Publicación' },
      { key: 'fecha', label: 'Fecha prevista', type: 'date', notion: 'Fecha', section: 'Publicación' },
      { key: 'pilar', label: 'Pilar', type: 'select', notion: 'Pilar', options: PILARES, section: 'Publicación' },
      { key: 'formato', label: 'Formato', type: 'select', notion: 'Formato', options: ['Reel', 'Carrusel', 'Historia', 'Post'], section: 'Publicación' },
      { key: 'estado', label: 'Estado', type: 'select', notion: 'Estado', options: ESTADOS_PUBLICACION, section: 'Publicación' },
      socia('socia', 'Responsable'),
      { key: 'idea', label: 'Idea principal', type: 'longtext', notion: 'Idea principal', section: 'Textos' },
      { key: 'guion', label: 'Guion', type: 'longtext', notion: 'Guion', section: 'Textos' },
      { key: 'copy', label: 'Copy', type: 'longtext', notion: 'Copy', section: 'Textos' },
      { key: 'hashtags', label: 'Hashtags', type: 'text', notion: 'Hashtags', section: 'Textos' },
      { key: 'portada', label: 'Portada o imagen', type: 'image', notion: 'Portada', section: 'Archivos', bucket: 'public' },
      { key: 'imagenes', label: 'Imágenes', type: 'files', notion: 'Imágenes', section: 'Archivos', bucket: 'public' },
      { key: 'diseno', label: 'Enlaces a archivos de diseño (Canva, Drive…)', type: 'files', notion: 'Archivos de diseño', section: 'Archivos' },
      { key: 'etiquetas', label: 'Etiquetas', type: 'multi', notion: 'Etiquetas', options: ['Evergreen', 'Reutilizable', 'Tendencia', 'Testimonio', 'Lanzamiento'], section: 'Archivos' },
      { key: 'propiedad', label: 'Propiedad relacionada', type: 'relation', relation: 'propiedades', single: true, notion: 'Propiedad', dual: 'publicaciones', section: 'Archivos' },
    ],
  },

  academia: {
    key: 'academia', db: 'DB_Academia_REVELA', env: 'NOTION_DB_ACADEMIA', prefix: 'ACA',
    label: 'Academia REVELA', singular: 'elemento',
    fields: [
      { key: 'titulo', label: 'Título', type: 'title', notion: 'Título', required: true, section: 'General' },
      { key: 'nivel', label: 'Nivel', type: 'select', notion: 'Nivel', options: ['Curso', 'Módulo', 'Clase', 'Recurso'], section: 'General' },
      { key: 'perteneceA', label: 'Pertenece a', type: 'relation', relation: 'academia', single: true, notion: 'Pertenece a', section: 'General', help: 'Clase → Módulo → Curso. Un recurso puede pertenecer a una clase.' },
      { key: 'categoria', label: 'Categoría', type: 'select', notion: 'Categoría', options: CATEGORIAS_ACADEMIA, section: 'General' },
      { key: 'tipoRecurso', label: 'Tipo de recurso', type: 'select', notion: 'Tipo de recurso', options: ['Plantilla', 'Checklist', 'Guía', 'Documento de trabajo', 'Ejercicio', 'Guion', 'Recurso de IA', 'Automatización / herramienta'], section: 'General', showIf: { key: 'nivel', in: ['Recurso'] } },
      { key: 'estado', label: 'Estado', type: 'select', notion: 'Estado', options: ESTADOS_ACADEMIA, section: 'General' },
      { key: 'fecha', label: 'Fecha prevista', type: 'date', notion: 'Fecha prevista', section: 'General' },
      socia('socia', 'Responsable'),
      { key: 'descripcion', label: 'Descripción', type: 'longtext', notion: 'Descripción', section: 'Contenido' },
      { key: 'objetivo', label: 'Objetivo de aprendizaje', type: 'longtext', notion: 'Objetivo', section: 'Contenido' },
      { key: 'temario', label: 'Temario / estructura', type: 'longtext', notion: 'Temario', section: 'Contenido' },
      { key: 'guion', label: 'Guion', type: 'longtext', notion: 'Guion', section: 'Contenido' },
      { key: 'ejemplos', label: 'Ejemplos prácticos', type: 'longtext', notion: 'Ejemplos prácticos', section: 'Contenido' },
      { key: 'ejercicios', label: 'Ejercicios', type: 'longtext', notion: 'Ejercicios', section: 'Contenido' },
      { key: 'material', label: 'Material de apoyo (PDF, plantillas)', type: 'files', notion: 'Material de apoyo', section: 'Recursos', bucket: 'private' },
      { key: 'enlaces', label: 'Videos, herramientas y recursos complementarios', type: 'files', notion: 'Enlaces', section: 'Recursos' },
      { key: 'proximas', label: 'Próximas acciones', type: 'longtext', notion: 'Próximas acciones', section: 'Recursos' },
      { key: 'tareas', label: 'Tareas', type: 'relation', relation: 'tareas', notion: 'Tareas', mirror: true, hidden: true },
    ],
  },

  documentos: {
    key: 'documentos', db: 'DB_Documentos', env: 'NOTION_DB_DOCUMENTOS', prefix: 'DOC',
    label: 'Documentos', singular: 'documento',
    fields: [
      { key: 'titulo', label: 'Título', type: 'title', notion: 'Título', required: true },
      { key: 'tipo', label: 'Tipo', type: 'select', notion: 'Tipo', options: TIPOS_DOCUMENTO },
      { key: 'estado', label: 'Estado', type: 'select', notion: 'Estado', options: ['Borrador', 'En revisión', 'Listo para firmar', 'Firmado'] },
      { key: 'cliente', label: 'Cliente', type: 'relation', relation: 'clientes', single: true, notion: 'Cliente', dual: 'documentos' },
      { key: 'propiedad', label: 'Propiedad', type: 'relation', relation: 'propiedades', single: true, notion: 'Propiedad', dual: 'documentos' },
      socia(),
      { key: 'fecha', label: 'Fecha', type: 'date', notion: 'Fecha' },
      { key: 'contenido', label: 'Texto del documento', type: 'longtext', notion: 'Contenido' },
      { key: 'archivo', label: 'Archivo (PDF firmado, escaneo)', type: 'files', notion: 'Archivo', bucket: 'private' },
      { key: 'version', label: 'Versión', type: 'number', notion: 'Versión' },
    ],
  },

  tareas: {
    key: 'tareas', db: 'DB_Tareas', env: 'NOTION_DB_TAREAS', prefix: 'TAR',
    label: 'Tareas', singular: 'tarea',
    fields: [
      { key: 'titulo', label: 'Tarea', type: 'title', notion: 'Tarea', required: true },
      { key: 'estado', label: 'Estado', type: 'select', notion: 'Estado', options: ESTADOS_TAREA },
      { key: 'area', label: 'Área', type: 'select', notion: 'Área', options: ['General', 'Propiedades', 'CRM', 'Marketing', 'Academia', 'Documentos', 'Oficina'] },
      socia(),
      { key: 'vence', label: 'Vence', type: 'date', notion: 'Vence' },
      { key: 'prioridad', label: 'Prioridad', type: 'select', notion: 'Prioridad', options: ['Alta', 'Media', 'Baja'] },
      { key: 'academia', label: 'Elemento de la Academia', type: 'relation', relation: 'academia', single: true, notion: 'Academia', dual: 'tareas' },
      { key: 'cliente', label: 'Cliente', type: 'relation', relation: 'clientes', single: true, notion: 'Cliente' },
      { key: 'propiedad', label: 'Propiedad', type: 'relation', relation: 'propiedades', single: true, notion: 'Propiedad' },
      { key: 'notas', label: 'Notas', type: 'longtext', notion: 'Notas' },
    ],
  },

  evaluaciones: {
    key: 'evaluaciones', db: 'DB_Evaluaciones', env: 'NOTION_DB_EVALUACIONES', prefix: 'EVA',
    label: 'Evaluaciones', singular: 'evaluación',
    fields: [
      { key: 'titulo', label: 'Título', type: 'title', notion: 'Título', required: true },
      { key: 'calculadora', label: 'Calculadora', type: 'select', notion: 'Calculadora', options: ['P.A.V.', 'Rentabilidad', 'Confort biofílico'] },
      { key: 'propiedad', label: 'Propiedad', type: 'relation', relation: 'propiedades', single: true, notion: 'Propiedad', dual: 'evaluaciones' },
      { key: 'resultado', label: 'Resultado', type: 'text', notion: 'Resultado' },
      { key: 'puntaje', label: 'Valor principal', type: 'number', notion: 'Valor principal' },
      { key: 'entradas', label: 'Entradas y supuestos', type: 'json', notion: 'Entradas' },
      socia(),
      { key: 'fecha', label: 'Fecha', type: 'date', notion: 'Fecha' },
    ],
  },
}

export const COLLECTION_KEYS = Object.keys(COLLECTIONS) as CollectionKey[]

/** Registro tal como lo ve la app */
export interface Rec {
  id: string
  ref?: string            // ID REVELA (PROP-12)
  createdAt?: string
  updatedAt?: string      // last_edited_time de Notion, se usa para detectar conflictos
  [key: string]: any
}

export function titleKey(col: CollectionKey): string {
  return COLLECTIONS[col].fields.find(f => f.type === 'title')!.key
}
